<template>
  <div class="interventions-page">
    <!-- Bouton flottant Mes Produits (Technicien uniquement) -->
    <button 
      v-if="userRole === 'Technicien'" 
      @click="showProductsModal = true"
      class="floating-products-btn"
      :class="{ 'has-products': myProducts.length > 0 }"
    >
      <div class="btn-content">
        <i class="fas fa-toolbox"></i>
        <span class="btn-label">Mon Sac</span>
        <span v-if="myProducts.length > 0" class="products-count">{{ myProducts.length }}</span>
      </div>
      <div class="btn-pulse"></div>
    </button>

    <!-- Modal Mes Produits -->
    <div v-if="showProductsModal" class="products-modal-overlay" @click="showProductsModal = false">
      <div class="products-modal" @click.stop>
        <div class="modal-header">
          <div class="header-content">
            <i class="fas fa-toolbox"></i>
            <div>
              <h3>Mon Sac de Produits</h3>
              <p>Produits disponibles pour vos interventions</p>
            </div>
          </div>
          <button @click="showProductsModal = false" class="close-btn">
            <i class="fas fa-times"></i>
          </button>
        </div>

        <div class="modal-body">
          <div v-if="loading" class="loading-state">
            <i class="fas fa-spinner fa-spin"></i>
            <p>Chargement...</p>
          </div>

          <div v-else-if="myProducts.length === 0" class="empty-state">
            <i class="fas fa-inbox"></i>
            <h4>Aucun produit dans votre sac</h4>
            <p>Contactez le magasinier pour obtenir des produits</p>
          </div>

          <div v-else class="products-grid">
            <div v-for="product in myProducts" :key="product._id" class="product-card">
              <div class="product-header">
                <div class="product-icon">
                  <i class="fas fa-box"></i>
                </div>
                <div class="product-quantity-badge">
                  <i class="fas fa-cubes"></i>
                  <span>{{ product.quantite }}</span>
                </div>
              </div>
              <div class="product-body">
                <h4>{{ product.designation }}</h4>
                <p class="product-ref" v-if="product.imei">
                  <i class="fas fa-barcode"></i>
                  IMEI: {{ product.imei }}
                </p>
                <p class="product-ref" v-else-if="product.iccid">
                  <i class="fas fa-sim-card"></i>
                  ICCID: {{ product.iccid }}
                </p>
                <p class="product-ref" v-else>
                  <i class="fas fa-barcode"></i>
                  {{ product.referenceInterne }}
                </p>
                <span class="product-category">{{ product.categorie }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <InterventionsManager 
      :user-role="userRole" 
      :user-id="userId"
      @intervention-updated="handleInterventionUpdated"
    />
  </div>
</template>

<script>
import { ref, computed, onMounted } from 'vue'
import { useStore } from 'vuex'
import InterventionsManager from '@/components/InterventionsManager.vue'
import stockService from '@/services/stockService'

export default {
  name: 'Interventions',
  components: {
    InterventionsManager
  },
  setup() {
    const store = useStore()
    
    const userRole = computed(() => store.getters['auth/userRole'])
    const userId = computed(() => store.getters['auth/user']?._id)
    const myProducts = ref([])
    const showProductsModal = ref(false)
    const loading = ref(false)

    const loadMyProducts = async () => {
      if (userRole.value !== 'Technicien') return
      
      try {
        const response = await stockService.getTechnicianProducts(userId.value)
        myProducts.value = response.data.products || []
      } catch (error) {
        console.error('Erreur chargement produits:', error)
      }
    }

    const handleInterventionUpdated = () => {
      // Rafraîchir les données si nécessaire
      console.log('Intervention mise à jour')
      loadMyProducts() // Recharger les produits après une intervention
    }

    onMounted(() => {
      loadMyProducts()
    })

    return {
      userRole,
      userId,
      myProducts,
      showProductsModal,
      loading,
      handleInterventionUpdated
    }
  }
}
</script>

<style scoped>
.interventions-page {
  height: 100%;
  display: flex;
  flex-direction: column;
  position: relative;
}

/* Bouton flottant */
.floating-products-btn {
  position: fixed;
  bottom: 30px;
  right: 30px;
  z-index: 999;
  background: linear-gradient(135deg, #3b82f6 0%, #1e40af 100%);
  border: none;
  border-radius: 50px;
  padding: 0;
  cursor: pointer;
  box-shadow: 0 8px 24px rgba(59, 130, 246, 0.4);
  transition: all 0.3s ease;
  overflow: hidden;
}

.floating-products-btn:hover {
  transform: translateY(-4px);
  box-shadow: 0 12px 32px rgba(59, 130, 246, 0.5);
}

.floating-products-btn.has-products {
  animation: pulse 2s infinite;
}

@keyframes pulse {
  0%, 100% {
    box-shadow: 0 8px 24px rgba(59, 130, 246, 0.4);
  }
  50% {
    box-shadow: 0 8px 32px rgba(59, 130, 246, 0.6);
  }
}

.btn-content {
  position: relative;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px 24px;
  color: white;
  font-weight: 600;
  font-size: 15px;
}

.btn-content i {
  font-size: 20px;
}

.btn-label {
  white-space: nowrap;
}

.products-count {
  background: white;
  color: #1e40af;
  padding: 4px 10px;
  border-radius: 20px;
  font-size: 13px;
  font-weight: 700;
  min-width: 28px;
  text-align: center;
}

.btn-pulse {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 100%;
  height: 100%;
  border-radius: 50px;
  background: rgba(255, 255, 255, 0.3);
  animation: ripple 2s infinite;
  pointer-events: none;
}

@keyframes ripple {
  0% {
    transform: translate(-50%, -50%) scale(1);
    opacity: 1;
  }
  100% {
    transform: translate(-50%, -50%) scale(1.5);
    opacity: 0;
  }
}

/* Modal */
.products-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  animation: fadeIn 0.3s ease;
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

.products-modal {
  background: white;
  border-radius: 16px;
  width: 90%;
  max-width: 900px;
  max-height: 85vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  animation: slideUp 0.3s ease;
}

@keyframes slideUp {
  from {
    transform: translateY(30px);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
}

.modal-header {
  padding: 24px;
  border-bottom: 1px solid #e5e7eb;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%);
  border-radius: 16px 16px 0 0;
}

.header-content {
  display: flex;
  align-items: center;
  gap: 16px;
}

.header-content > i {
  width: 56px;
  height: 56px;
  background: linear-gradient(135deg, #3b82f6 0%, #1e40af 100%);
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 24px;
}

.header-content h3 {
  margin: 0 0 4px 0;
  color: #111827;
  font-size: 22px;
}

.header-content p {
  margin: 0;
  color: #6b7280;
  font-size: 14px;
}

.close-btn {
  width: 40px;
  height: 40px;
  border: none;
  background: white;
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s;
  color: #6b7280;
}

.close-btn:hover {
  background: #f3f4f6;
  color: #111827;
  transform: rotate(90deg);
}

.modal-body {
  padding: 24px;
  overflow-y: auto;
  flex: 1;
}

.loading-state,
.empty-state {
  text-align: center;
  padding: 60px 20px;
}

.loading-state i {
  font-size: 48px;
  color: #3b82f6;
  margin-bottom: 16px;
}

.empty-state i {
  font-size: 64px;
  color: #d1d5db;
  margin-bottom: 16px;
}

.empty-state h4 {
  margin: 0 0 8px 0;
  color: #374151;
  font-size: 18px;
}

.empty-state p {
  margin: 0;
  color: #9ca3af;
  font-size: 14px;
}

.products-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
  gap: 20px;
}

.product-card {
  background: white;
  border: 2px solid #e5e7eb;
  border-radius: 12px;
  overflow: hidden;
  transition: all 0.3s;
}

.product-card:hover {
  border-color: #3b82f6;
  transform: translateY(-4px);
  box-shadow: 0 8px 24px rgba(59, 130, 246, 0.15);
}

.product-header {
  background: linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%);
  padding: 16px;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.product-icon {
  width: 48px;
  height: 48px;
  background: linear-gradient(135deg, #3b82f6 0%, #1e40af 100%);
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 20px;
}

.product-quantity-badge {
  display: flex;
  align-items: center;
  gap: 6px;
  background: white;
  padding: 8px 14px;
  border-radius: 20px;
  color: #059669;
  font-weight: 700;
  font-size: 16px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.product-quantity-badge i {
  font-size: 14px;
}

.product-body {
  padding: 16px;
}

.product-body h4 {
  margin: 0 0 8px 0;
  color: #111827;
  font-size: 16px;
  font-weight: 600;
}

.product-ref {
  display: flex;
  align-items: center;
  gap: 6px;
  margin: 0 0 12px 0;
  color: #6b7280;
  font-size: 13px;
  font-family: 'Courier New', monospace;
}

.product-ref i {
  color: #9ca3af;
}

.product-category {
  display: inline-block;
  padding: 4px 12px;
  background: #dbeafe;
  color: #1e40af;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 600;
}
</style>
